'use client'
import type { ReactNode } from 'react'
import { Button, type ButtonProps } from '@chakra-ui/react'
import type { FieldValues } from 'react-hook-form'
import { useGrandFormContext } from '../FormField'

interface GrandFormSubmitButtonProps extends Omit<
  ButtonProps,
  'type' | 'isLoading' | 'isDisabled'
> {
  children: ReactNode
  /** Disable when form has validation errors. Default: true. */
  disableIfInvalid?: boolean
  /** Disable when form is unchanged. Default: false. */
  disableIfPristine?: boolean
  /** Override the form's `isSubmitting` flag. */
  loading?: boolean
  /** Override the form's computed disabled state. */
  disabled?: boolean
  /** Text shown while submitting. */
  loadingText?: string
}

export const GrandFormSubmitButton = <TFieldValues extends FieldValues = FieldValues>({
  children,
  disableIfInvalid = true,
  disableIfPristine = false,
  loading: customLoading,
  disabled: customDisabled,
  loadingText,
  ...buttonProps
}: GrandFormSubmitButtonProps) => {
  const { formState } = useGrandFormContext<TFieldValues>()

  const { isSubmitting, isValid, isDirty } = formState

  const shouldDisable =
    customDisabled || (disableIfInvalid && !isValid) || (disableIfPristine && !isDirty)

  const isLoading = customLoading !== undefined ? customLoading : isSubmitting

  return (
    <Button
      isDisabled={shouldDisable}
      isLoading={isLoading}
      loadingText={loadingText}
      type="submit"
      {...buttonProps}
    >
      {children}
    </Button>
  )
}
